/**
 * A billable item represents a billable product or service that can be added to invoices.
 * You can create, manage, and organize these items to streamline your billing process.
 * Billable items include details like name, description, price, and tax information.
 */
export interface BillableItem {
  /**
   * Billable item ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the billable item is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the billable item.
   *
   * Ignored when creating a billable item.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the billable item was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the billable item was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Billable item name, for display on invoices.
   * @minLength 1
   * @maxLength 200
   */
  name?: string;
  /**
   * Detailed description of the billable item. Provides additional information about the product or service.
   * @minLength 1
   * @maxLength 600
   */
  description?: string | null;
  /**
   * Price of the billable item in decimal format with up to 4 decimal places.
   * Represents the base cost before taxes are applied. This is a required field when creating a new billable item.
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:-1000000000000000, lte:1000000000000000, maxScale:4 }
   */
  price?: string;
  /**
   * Reference to a tax group that defines the tax rules applicable to this billable item.
   * Use the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.
   * The tax group determines how taxes are calculated when this item is added to an invoice.
   * If not specified, the default tax rules for the business will apply.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Billable item image, stored in the Wix Media Manager.
   * Upload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.
   */
  image?: Image;
  /**
   * Custom field data for the billable item.
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Tags. */
  tags?: Tags;
}

export interface Image {
  /** WixMedia image ID. */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /** Image alt text. */
  altText?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateBillableItemRequest {
  /** Billable item to create. */
  billableItem: BillableItem;
}

export interface CreateBillableItemResponse {
  /** Created billable item. */
  billableItem?: BillableItem;
}

export interface GetBillableItemRequest {
  /**
   * Billable item ID.
   * @format GUID
   */
  billableItemId: string;
}

export interface GetBillableItemResponse {
  /** Requested billable item. */
  billableItem?: BillableItem;
}

export interface UpdateBillableItemRequest {
  /** Billable item to update. May be partial. */
  billableItem: BillableItem;
}

export interface UpdateBillableItemResponse {
  /** Updated billable item. */
  billableItem?: BillableItem;
}

export interface DeleteBillableItemRequest {
  /**
   * Billable item ID to delete.
   * @format GUID
   */
  billableItemId: string;
}

export interface DeleteBillableItemResponse {}

export interface QueryBillableItemsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryBillableItemsResponse {
  /** Requested billable items. */
  billableItems?: BillableItem[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface InternalQueryBillableItemsRequest {
  /** Query options. */
  query?: InternalCursorQuery;
}

export interface InternalCursorQuery
  extends InternalCursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: InternalCursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface InternalCursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: InternalCursorPaging;
}

export interface InternalCursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 10000
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface InternalQueryBillableItemsResponse {
  /** Requested billable items. */
  billableItems?: BillableItem[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface BulkCreateBillableItemsRequest {
  /**
   * List of billable items to create.
   * @minSize 1
   * @maxSize 100
   */
  billableItems: BillableItem[];
  /** Whether to return the created billable items in the response. */
  returnEntity?: boolean;
}

export interface BulkCreateBillableItemsResponse {
  /**
   * List of billable items create operation results including metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkBillableItemResult[];
  /** Metadata regarding the bulk create operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkBillableItemResult {
  /** Billable item's create operation metadata. */
  itemMetadata?: ItemMetadata;
  /** Created billable item if `returnEntity` was set `true` in the request. */
  item?: BillableItem;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateBillableItemsRequest {
  /**
   * Billable items to update.
   * @minSize 1
   * @maxSize 100
   */
  billableItems: MaskedBillableItem[];
  /** Return updated billable items in the response. */
  returnEntity?: boolean;
}

export interface MaskedBillableItem {
  /** Billable items to update. May be partial. */
  billableItem?: BillableItem;
}

export interface BulkUpdateBillableItemsResponse {
  /**
   * Billable items update operation results including metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBillableItemsResponseBulkBillableItemResult[];
  /** Metadata regarding the bulk update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateBillableItemsResponseBulkBillableItemResult {
  /** Billable item's update operation metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated billable item if `returnEntity` was set `true` in the request. */
  item?: BillableItem;
}

export interface BulkDeleteBillableItemsRequest {
  /**
   * Billable item IDs to delete.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  billableItemIds: string[];
}

export interface BulkDeleteBillableItemsResponse {
  /**
   * Billable items delete operation results including metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkDeleteBillableItemsResponseBulkBillableItemResult[];
  /** Metadata regarding the bulk delete operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteBillableItemsResponseBulkBillableItemResult {
  /** Billable item's delete operation metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateBillableItemTagsRequest {
  /**
   * IDs of billable items which tags will be updated.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  billableItemIds: string[];
  /** List of tags to assign. */
  assignTags?: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateBillableItemTagsResponse {
  /**
   * Billable items update tags results including metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBillableItemTagsResult[];
  /** Bulk update tags metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateBillableItemTagsResult {
  /** Billable item's update tags operation metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateBillableItemTagsByFilterRequest {
  /** Filter to select relevant billable items. */
  filter: Record<string, any> | null;
  /** List of tags to assign. */
  assignTags?: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateBillableItemTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

export interface SearchBillableItemsRequest {
  /** Search options. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor pointing to page of results.
   * When requesting 'cursor_paging.cursor', no `filter`, `sort` or `search` can be provided.
   */
  cursorPaging?: CursorPaging;
  /** A filter object. See documentation [here](https://bo.wix.com/wix-docs/rnd/platformization-guidelines/api-query-language#platformization-guidelines_api-query-language_defining-in-protobuf) */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the form [{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Aggregations | Faceted search: refers to a way to explore large amounts of data by displaying summaries about various partitions of the data and later allowing to narrow the navigation to a specific partition.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields */
  search?: SearchDetails;
  /**
   * UTC offset or IANA time zone. Valid values are
   * ISO 8601 UTC offsets, such as +02:00 or -06:00,
   * and IANA time zone IDs, such as Europe/Rome
   *
   * Affects all filters and aggregations returned values.
   * You may override this behavior in a specific filter by providing
   * timestamps including time zone. e.g. `"2023-12-20T10:52:34.795Z"`
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor pointing to page of results.
   * When requesting 'cursor_paging.cursor', no `filter`, `sort` or `search` can be provided.
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** Value aggregation */
  value?: ValueAggregation;
  /** Range aggregation */
  range?: RangeAggregation;
  /** Scalar aggregation */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation */
  nested?: NestedAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dot notation to specify json path
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Value aggregation */
  value?: ValueAggregation;
  /** Range aggregation */
  range?: RangeAggregation;
  /** Scalar aggregation */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if to is not given */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if from is not given */
  to?: number | null;
}

export enum SortType {
  /** Should sort by number of matches */
  COUNT = 'COUNT',
  /** Should sort by value of the field alphabetically */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Should sort in descending order */
  DESC = 'DESC',
  /** Should sort in ascending order */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Should missing values be excluded from the aggregation results */
  EXCLUDE = 'EXCLUDE',
  /** Should missing values be included in the aggregation results */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Can specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Count of distinct values */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value */
  MIN = 'MIN',
  /** Maximum value */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values */
  includeOptions?: IncludeMissingValuesOptions;
  /** Should sort by number of matches or value of the field */
  sortType?: SortTypeWithLiterals;
  /** Should sort in ascending or descending order */
  sortDirection?: SortDirectionWithLiterals;
  /** How many aggregations would you like to return? Can be between 1 and 250. 10 is the default. */
  limit?: number | null;
  /** Should missing values be included or excluded from the aggregation results. Default is EXCLUDE */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket */
  RANGE = 'RANGE',
  /** A single-value metric aggregation - e.g. min, max, sum, avg */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.) */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets, where during aggregation each entity will be placed in the first bucket where its value falls into based on provided range bounds
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Define the operator for the scalar aggregation */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval */
  YEAR = 'YEAR',
  /** Monthly interval */
  MONTH = 'MONTH',
  /** Weekly interval */
  WEEK = 'WEEK',
  /** Daily interval */
  DAY = 'DAY',
  /** Hourly interval */
  HOUR = 'HOUR',
  /** Minute interval */
  MINUTE = 'MINUTE',
  /** Second interval */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Value aggregation */
  value?: ValueAggregation;
  /** Range aggregation */
  range?: RangeAggregation;
  /** Scalar aggregation */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation */
  dateHistogram?: DateHistogramAggregation;
  /**
   * User-defined name of aggregation, should be unique, will appear in aggregation results
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation, client must provide matching aggregation field below */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by, use dont notation to specify json path
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Value aggregation */
  value?: ValueAggregation;
  /** Range aggregation */
  range?: RangeAggregation;
  /** Scalar aggregation */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** An aggregation where result buckets are dynamically built - one per unique value */
  VALUE = 'VALUE',
  /** An aggregation, where user can define set of ranges - each representing a bucket */
  RANGE = 'RANGE',
  /** A single-value metric aggregation - e.g. min, max, sum, avg */
  SCALAR = 'SCALAR',
  /** An aggregation, where result buckets are dynamically built - one per time interval (hour, day, week, etc.) */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Multi-level aggregation, where each next aggregation is nested within previous one */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within previous one
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface SearchDetails {
  /** Defines how separate search terms in `expression` are combined */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If empty - will search in all searchable fields. Use dot notation to specify json path
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Flag if should use auto fuzzy search (allowing typos by a managed proximity algorithm) */
  fuzzy?: boolean;
}

export enum Mode {
  /** Any of the search terms must be present */
  OR = 'OR',
  /** All search terms must be present */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchBillableItemsResponse {
  /** Requested billable items. */
  billableItems?: BillableItem[];
  /** Paging metadata. */
  pagingMetadata?: PagingMetadataV2;
  /** Response aggregation data. */
  aggregationData?: AggregationData;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface AggregationData {
  /**
   * key = aggregation name (as derived from search request)
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field
   * @maxLength 100
   */
  value?: string;
  /** Count of entities with this value */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range */
  from?: number | null;
  /** Exclusive upper bound of the range */
  to?: number | null;
  /** Count of entities in this range */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results */
  values?: ValueResults;
  /** Range aggregation results */
  ranges?: RangeResults;
  /** Scalar aggregation results */
  scalar?: AggregationResultsScalarResult;
  /**
   * User-defined name of aggregation, matches the one provided in request
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that matches result */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by, matches the one provided in request
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results */
  values?: ValueResults;
  /** Range aggregation results */
  ranges?: RangeResults;
  /** Scalar aggregation results */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field
   * @maxLength 1000
   */
  value?: string;
  /** Count of entities with this value */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range */
  from?: number | null;
  /** Exclusive upper bound of the range */
  to?: number | null;
  /** Count of entities in this range */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result */
  value?: ValueResult;
  /** Range aggregation result */
  range?: RangeResult;
  /** Scalar aggregation result */
  scalar?: ScalarResult;
  /** Date histogram aggregation result */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result */
  value?: ValueResult;
  /** Range aggregation result */
  range?: RangeResult;
  /** Scalar aggregation result */
  scalar?: ScalarResult;
  /** Date histogram aggregation result */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in ISO 8601 format
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form
 * aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results */
  values?: ValueResults;
  /** Range aggregation results */
  ranges?: RangeResults;
  /** Scalar aggregation results */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results */
  nested?: NestedResults;
  /**
   * User-defined name of aggregation as derived from search request
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that must match provided kind as derived from search request */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by as derived from search request
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results */
  values?: ValueResults;
  /** Range aggregation results */
  ranges?: RangeResults;
  /** Scalar aggregation results */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results */
  nested?: NestedResults;
}

/** @docsIgnore */
export type CreateBillableItemApplicationErrors =
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_GROUP_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateBillableItemTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateBillableItemTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
